import React, { useEffect, useState, useCallback } from 'react';
import '../styles/TaskMarketplace.css';
import TaskDialog from './TaskDialog';
import { API_BASE_URL } from '../config';

function TaskMarketplace() {
  const [selectedTask, setSelectedTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('available');
  const [filterPriority, setFilterPriority] = useState('all');

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params
      const params = new URLSearchParams();
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      if (filterPriority !== 'all') {
        params.append('priority', filterPriority);
      }

      const url = `${API_BASE_URL}/tasks${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch tasks');
      }

      const data = await response.json();
      setTasks(data.data || data.tasks || []);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleClaimClick = (task) => {
    setSelectedTask(task);
  };

  const handleCloseDialog = () => {
    setSelectedTask(null);
  };

  const handleTaskClaimed = () => {
    setSelectedTask(null);
    fetchTasks(); // Refresh the task list
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#ef4444';
      case 'medium':
        return '#f59e0b';
      case 'low':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const getComplexityBadge = (complexity) => {
    switch (complexity) {
      case 'simple':
        return '⚡';
      case 'moderate':
        return '⚙️';
      case 'complex':
        return '🔥';
      default:
        return '📋';
    }
  };

  return (
    <div className="task-marketplace">
      <div className="marketplace-header">
        <h1>Task Marketplace</h1>
        <p className="marketplace-subtitle">Browse and claim individual tasks from projects</p>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Tasks</option>
            <option value="available">Available</option>
            <option value="claimed">Claimed</option>
            <option value="in_progress">In Progress</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Priority:</label>
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
            <option value="all">All Priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {loading && (
        <div className="loading-message">Loading available tasks...</div>
      )}

      {error && (
        <div className="error-message">{error}</div>
      )}

      {!loading && !error && tasks.length === 0 && (
        <div className="empty-message">No tasks match your filters.</div>
      )}

      <div className="tasks-grid">
        {tasks.map((task) => (
          <div key={task.id} className="task-card">
            <div className="task-header">
              <div className="task-badges">
                <span
                  className="priority-badge"
                  style={{ backgroundColor: getPriorityColor(task.priority) }}
                >
                  {task.priority}
                </span>
                <span className="complexity-badge">
                  {getComplexityBadge(task.complexity)} {task.complexity}
                </span>
              </div>
              <div className="task-status">{task.status.replace('_', ' ')}</div>
            </div>

            <h3 className="task-title">{task.title}</h3>

            <p className="task-description">
              {task.description.length > 150
                ? `${task.description.substring(0, 150)}...`
                : task.description}
            </p>

            <div className="task-meta">
              <div className="task-project">
                <span className="meta-label">Project:</span>
                <span className="meta-value">{task.problem?.title || 'Unknown'}</span>
              </div>
              <div className="task-category">
                <span className="meta-label">Category:</span>
                <span className="meta-value">{task.problem?.category || 'N/A'}</span>
              </div>
            </div>

            <div className="task-footer">
              <div className="task-bounty">
                <span className="bounty-label">Bounty:</span>
                <span className="bounty-amount">${task.bounty_portion?.toFixed(2) || '0.00'}</span>
              </div>
              {task.estimated_hours && (
                <div className="task-hours">
                  <span className="hours-label">Est. Hours:</span>
                  <span className="hours-value">{task.estimated_hours}h</span>
                </div>
              )}
              {task.status === 'available' && (
                <button
                  className="claim-button"
                  onClick={() => handleClaimClick(task)}
                >
                  Claim Task
                </button>
              )}
              {task.status !== 'available' && (
                <button
                  className="view-button"
                  onClick={() => handleClaimClick(task)}
                >
                  View Details
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {selectedTask && (
        <TaskDialog
          task={selectedTask}
          onClose={handleCloseDialog}
          onTaskClaimed={handleTaskClaimed}
        />
      )}
    </div>
  );
}

export default TaskMarketplace;
